import React, { useEffect, useState } from 'react';
import { useForm } from "react-hook-form";
import { useMutation } from 'react-query';

import bgLogin from "../../../assets/images/bg-login.jpg"
import { InputToggle, AppIcon, Snackbar } from '../../../shared/components';
import { getAuthConfig, login } from '../../../services/api-auth';

/**
 * Google's "G", drawn inline rather than fetched, so the button cannot be left
 * iconless by a blocked request and needs nothing added to the build.
 */
const GoogleMark = () => (
  <svg className="w-5 h-5 shrink-0" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"/>
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"/>
    <path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"/>
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"/>
  </svg>
);

const Login = () => {
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isShowErrorMessage, setIsShowErrorMessage] = useState(false);
  const { handleSubmit, register } = useForm();

  // Whether to draw the Google button at all. Starts hidden and is only ever turned on
  // by a server that says it has its Google settings (CV-844), so a server without them
  // — production, until it is switched on — shows the password box and nothing else.
  // If the question cannot be asked at all, it stays hidden: never offer a door that
  // might not open.
  const [isGoogleOffered, setIsGoogleOffered] = useState(false);

  useEffect(() => {
    let stillOnScreen = true;
    getAuthConfig()
      .then(({ data }) => { if (stillOnScreen) setIsGoogleOffered(Boolean(data && data.google)); })
      .catch(() => { if (stillOnScreen) setIsGoogleOffered(false); });
    return () => { stillOnScreen = false; };
  }, []);

  const mutation = useMutation(login, {
    onSuccess: async () => window.location.href = '/',
    onError: async () => setIsShowErrorMessage(true)
  })

  return <main className="flex justify-center fixed w-full h-full">
    <div className="hidden md:flex flex-1 w-px relative items-center text-white">
      <img
        className="object-cover absolute inset-0 h-full w-full brightness-70"
        src={bgLogin} alt="Documenting street art from across the world"
      />
      < h2 className="hidden font-helvetica-lt font-bold lg:block relative uppercase text-[63px] leading-[54px] xl:text-[75px] xl:leading-[65px] px-12 w-4/5 xl:w-2/3" >
        Documenting street art from across the world
      </h2>
    </div>

    <form className="container-form max-w-[360px]" onSubmit={handleSubmit(mutation.mutate)}>
      <AppIcon name="logo" className="mb-12 w-40 mx-auto"/>

      <h1 className="mb-6 text-xl font-medium font-helvetica text-center md:text-left">
        Log in to my account
      </h1>

      <input
        {...register("email", { required: true })}
        type="email"
        autoComplete="email"
        className="input-default text-[15px] font-helvetica mb-4"
        placeholder="Enter email"
      />
    
      <div className="relative flex items-center">
        <input
          {...register("password", { required: true })}
          autoComplete="current-password"
          type={isShowPassword ? 'text' : 'password'}
          className="input-default text-[15px] font-helvetica"
          placeholder="Enter password"
        />
        <button
          type="button"
          className="text-gray-500 absolute right-2.5 cursor-pointer"
          onClick={() => setIsShowPassword(!isShowPassword)}
        >
          { isShowPassword
            ? <AppIcon name="visibility-off" className="w-5"/>
            : <AppIcon name="visibility-on" className="w-5"/>
          }
        </button>
      </div>
    
      <div className="mt-7 flex justify-between text-xs font-helvetica">
        <InputToggle label="Remember me" register={register("remember")} />
        <a className="text-blue" href="/app/#/forgot-password">Forgot password?</a>
      </div>

      <input
        className="btn-default text-white bg-secondary hover:bg-green-400 mt-8"
        type="submit" value="Log in"
      />

      { isGoogleOffered &&
        <>
          <div className="mt-6 flex items-center gap-3 text-xs font-helvetica text-black-900">
            <span className="h-px flex-1 bg-black-100"/>
            <span>or</span>
            <span className="h-px flex-1 bg-black-100"/>
          </div>

          {/*
            A plain link, not a fetch. The whole Google handshake happens on the server
            (controllers/authGoogle.js), which is why this screen needs no Google library
            and no keys in the browser — and why logging in this way cannot be broken by
            anything in here going wrong.
          */}
          <a
            className="btn-default mt-6 flex items-center justify-center gap-3 border border-black-100 bg-white text-black-900 hover:bg-gray-50 no-underline"
            href="/auth/google"
          >
            <GoogleMark/>
            <span className="font-helvetica text-[15px]">Log in with Google</span>
          </a>
        </>
      }
  
      <p className="mt-8 pt-7 border-t flex gap-2 justify-center border-t-black-100 text-xs font-helvetica">
        <span className="text-black-900">Don't have an account?</span>
        <a className="text-blue" target="_blank" href="https://www.canvsart.com/contributor" rel="noreferrer">Request to join</a>
      </p>
    </form>
    { isShowErrorMessage &&
      <Snackbar
        message="Your email or password is incorrect"
        type="error"
        onClose={() => setIsShowErrorMessage(false)}
      />
    }
  </main>;
}

export default Login;